import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ClipboardList, CalendarClock, StickyNote, CheckCircle2, Send, AtSign, ListTodo, ChevronDown, ChevronRight } from "lucide-react";

const TRACK_COLORS = { on_track: "#10B981", at_risk: "#F59E0B", jeopardy: "#EF4444" };

export default function MyCard() {
  const { token } = useParams();
  const [card, setCard] = useState(null);
  const [error, setError] = useState("");
  const [collapsedPj, setCollapsedPj] = useState({});

  useEffect(() => {
    api.get(`/public/card/${token}`)
      .then(({ data }) => setCard(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "This link is invalid."));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <p className="text-muted-foreground" data-testid="mycard-error">{error}</p>
      </div>
    );
  }
  if (!card) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const total = card.open_tasks + card.open_actions;
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <span className="font-display text-lg font-extrabold tracking-tight">Project Management <span className="text-primary">Orchestrator</span></span>
          <span className="text-xs text-muted-foreground">Progress update card</span>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-10" data-testid="mycard-page">
        <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="mycard-person">Hi {card.person},</h1>
        <p className="text-muted-foreground mt-2">
          You have <strong className="text-foreground">{total} open item{total === 1 ? "" : "s"}</strong>
          {card.overdue > 0 && <span className="text-red-500 font-semibold"> ({card.overdue} overdue)</span>}.
          Update your % complete below — it goes straight to {card.pm_name || "your PM"} for confirmation.
        </p>

        {card.note && (
          <div className="mt-5 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 flex gap-2.5" data-testid="mycard-note">
            <StickyNote className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p className="text-sm"><strong>Note from {card.pm_name || "your PM"}:</strong> {card.note}</p>
          </div>
        )}

        {(card.mentions || []).length > 0 && (
          <div className="mt-5 space-y-2" data-testid="mycard-mentions">
            {card.mentions.map((mn, i) => (
              <div key={i} className="rounded-lg border border-violet-300 bg-violet-50 px-4 py-3 flex gap-2.5" data-testid={`mycard-mention-${i}`}>
                <AtSign className="h-4 w-4 text-violet-600 shrink-0 mt-0.5" />
                <p className="text-sm text-violet-900">
                  <strong>{mn.author || "Your PM"}</strong> mentioned you in <strong>#{mn.space_name}</strong>: “{mn.snippet}”{" "}
                  <span className="text-violet-500 text-xs">({(mn.created_at || "").slice(0, 10)})</span>
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 space-y-8">
          {card.projects.map((pj) => (
            <div key={pj.project_id} data-testid={`mycard-project-${pj.project_id}`}>
              <div className="flex items-center gap-2 rounded-md border-l-4 border-primary bg-primary/5 px-3 py-2 mb-3" data-testid={`mycard-project-header-${pj.project_id}`}>
                <ClipboardList className="h-3.5 w-3.5 text-primary shrink-0" />
                <p className="text-xs font-bold text-primary uppercase tracking-wider truncate">{pj.project_name}</p>
                <span className="ml-auto text-[10px] text-muted-foreground shrink-0">{pj.tasks.length + pj.actions.length} item{pj.tasks.length + pj.actions.length === 1 ? "" : "s"}</span>
              </div>
              <div className="space-y-3">
                {pj.tasks.length > 0 && (
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5" data-testid={`mycard-tasks-header-${pj.project_id}`}>
                    <ListTodo className="h-3.5 w-3.5" /> Tasks ({pj.tasks.length})
                  </p>
                )}
                {pj.tasks.map((t) => <TaskRow key={t.id} task={t} token={token} />)}
                {pj.actions.length > 0 && (
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 pt-1" data-testid={`mycard-actions-header-${pj.project_id}`}>
                    <ClipboardList className="h-3.5 w-3.5" /> Action Items ({pj.actions.length})
                  </p>
                )}
                {pj.actions.map((a) => <ActionRow key={a.id} action={a} token={token} />)}
                {pj.trueups.length > 0 && (
                  <p className="text-xs text-amber-600 font-semibold flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> A progress true-up is due — please submit your % on the tasks above.</p>
                )}
              </div>
              )}
            </div>
          ))}
          {card.projects.length === 0 && <p className="text-muted-foreground text-sm" data-testid="mycard-empty">Nothing open right now — you're all caught up. 🎉</p>}
        </div>
        <p className="text-xs text-muted-foreground mt-12">Sent via Project Management Orchestrator. Your submissions await PM confirmation before the plan updates.</p>
      </main>
    </div>
  );
}

function ActionRow({ action, token }) {
  const [status, setStatus] = useState(action.status || "open");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pendingDone, setPendingDone] = useState(false);
  const [outcome, setOutcome] = useState(action.outcome || "");
  const done = status === "done" && !pendingDone;

  const submit = async (s, note) => {
    const prev = status;
    setStatus(s);
    setBusy(true);
    try {
      await api.post(`/public/card/${token}/action`, { action_id: action.id, status: s, outcome: note || "" });
      setSaved(true);
      setPendingDone(false);
      toast.success(s === "done" ? "Marked complete — your PM can see it" : "Status updated");
    } catch (e) {
      setStatus(prev);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  const onPick = (v) => {
    if (v === "done") { setPendingDone(true); return; }
    setPendingDone(false);
    submit(v);
  };

  return (
    <div className="border border-border rounded-lg px-4 py-3" data-testid={`mycard-action-${action.id}`}>
      <div className="flex items-center gap-2.5">
        <ClipboardList className={`h-4 w-4 shrink-0 ${done ? "text-blue-600" : action.bucket === "late" ? "text-red-500" : "text-muted-foreground"}`} />
        {action.display_id && <span className="font-mono text-[10px] font-bold text-primary shrink-0">{action.display_id}</span>}
        <span className={`text-sm font-medium flex-1 min-w-0 truncate ${done ? "line-through text-muted-foreground" : ""}`}>{action.title}</span>
        {action.due_date && <span className={`text-xs shrink-0 ${!done && action.bucket === "late" ? "text-red-500 font-bold" : "text-muted-foreground"}`}>due {action.due_date}</span>}
      </div>
      <div className="flex items-center gap-2.5 mt-2.5">
        <span className="text-xs text-muted-foreground">Action item — set your status:</span>
        <select
          value={pendingDone ? "done" : status}
          onChange={(e) => onPick(e.target.value)}
          disabled={busy}
          className="h-8 rounded border border-border bg-background px-2 text-sm"
          data-testid={`mycard-action-status-${action.id}`}
        >
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="done">Done</option>
        </select>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> :
          saved && !pendingDone && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid={`mycard-action-saved-${action.id}`}><CheckCircle2 className="h-4 w-4" /> Saved</span>}
      </div>
      {pendingDone && (
        <div className="mt-2.5 space-y-2" data-testid={`mycard-action-outcome-box-${action.id}`}>
          <textarea
            rows={2}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value.slice(0, 500))}
            placeholder="Optional — briefly note the outcome (what was done / result)…"
            className="w-full rounded border border-border bg-background px-2.5 py-1.5 text-sm resize-none"
            data-testid={`mycard-action-outcome-${action.id}`}
          />
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-8 gap-1.5" onClick={() => submit("done", outcome)} disabled={busy} data-testid={`mycard-action-complete-${action.id}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Mark complete
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setPendingDone(false)} disabled={busy} data-testid={`mycard-action-cancel-${action.id}`}>Cancel</Button>
          </div>
        </div>
      )}
      {done && action.outcome && !pendingDone && <p className="text-xs text-muted-foreground italic mt-1.5">Outcome: {action.outcome}</p>}
    </div>
  );
}

function TaskRow({ task, token }) {
  const [pct, setPct] = useState(task.owner_submitted && task.proposed_percent != null ? task.proposed_percent : task.percent_complete);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const pending = !submitted && task.owner_submitted && task.proposed_percent != null && Number(pct) === Number(task.proposed_percent);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/public/card/${token}/progress`, { task_id: task.id, percent: Number(pct) });
      setSubmitted(true);
      toast.success("Submitted — awaiting PM confirmation");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-border rounded-lg px-4 py-3" data-testid={`mycard-task-${task.id}`}>
      <div className="flex items-center gap-2.5">
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: task.overdue ? "#EF4444" : TRACK_COLORS[task.track_status] || "#64748b" }} />
        <span className="text-sm font-medium flex-1 min-w-0 truncate">{task.is_milestone ? "◆ " : ""}{task.title}</span>
        {task.end_date && <span className={`text-xs shrink-0 ${task.overdue ? "text-red-500 font-bold" : "text-muted-foreground"}`}>due {task.end_date}</span>}
      </div>
      <div className="flex items-center gap-3 mt-3">
        <input
          type="range" min="0" max="100" step="5" value={pct}
          onChange={(e) => { setPct(e.target.value); setSubmitted(false); }}
          className="flex-1 accent-primary"
          disabled={task.is_milestone}
          data-testid={`mycard-slider-${task.id}`}
        />
        <span className="text-sm font-bold tabular-nums w-12 text-right">{pct}%</span>
        {submitted ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 shrink-0" data-testid={`mycard-submitted-${task.id}`}><CheckCircle2 className="h-4 w-4" /> Sent</span>
        ) : pending ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 shrink-0" title={`You submitted ${task.proposed_percent}% — your PM will confirm it in the next true-up`} data-testid={`mycard-pending-${task.id}`}>
            <CheckCircle2 className="h-4 w-4" /> Sent — awaiting PM
          </span>
        ) : (
          <Button size="sm" className="gap-1.5 shrink-0" onClick={submit} disabled={submitting || task.is_milestone} data-testid={`mycard-submit-${task.id}`}>
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Submit
          </Button>
        )}
      </div>
    </div>
  );
}
